// Package Imports
import React from "react";
import "chartjs-plugin-datalabels";
import { BeatLoader } from "react-spinners";
import {
  InputLabel,
  FormControl,
  Select,
  Card,
  Button,
  Box,
  ButtonGroup,
  OutlinedInput,
  Menu,
  MenuItem,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import BoltIcon from "@mui/icons-material/Bolt";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import BarChartIcon from "@mui/icons-material/BarChart";
import { MdCloudDownload } from "react-icons/md";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { styled } from "@mui/material/styles";

// Local Imports
import "../style/reactsweetstyle.css";
import "../style/Home.css";
import {
  CalculateActiveMetrics,
  CalculateEnergyConsumption,
  CalculateRevenue,
  CalculateTotalSessions,
  CalculateUtilization,
  GetSessions,
} from "./../lib/Calculations";
import { FilterData, FilterStations, StationGroups } from "./../lib/Filtering";
import { ExportData, SetVictoryLabels } from "./../lib/Graphing";
import Revenue from "../components/graphs/Revenue.js";
import UniqueUsers from "../components/graphs/UniqueUsers.js";
import EnergyConsumption from "../components/graphs/EnergyConsumption.js";
import Utilization from "../components/graphs/Utilization.js";
import TimeframeSelect from "../components/graphs/TimeframeSelect.js";

// Contants
const _ = require("underscore");

class HomeContainer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      // Graph Selection
      loading: true,
      selectedGraph: "totalSessions",
      selectedGroup: "All Groups",
      bigChart: <UniqueUsers />,

      // Timeframe Selection
      timeframe: "CurrentMonth",
      start: new Date(),
      end: new Date(),

      // Export
      exportData: [],

      // Total Sessions
      sessionsPerDateArray: [],
      totalSessions: <BeatLoader color="#9e9e9e" size={10} />,

      // Energy Consumption
      energyPerDateArray: [],
      consumptionUnit: "kWh",
      energyConsumption: <BeatLoader color="#9e9e9e" size={10} />,

      // Active Energy Consumption
      activeConsumptionUnit: "kWh",
      activeConsumption: <BeatLoader color="#9e9e9e" size={10} />,

      // Revenue
      revenuePerDateArray: [],
      revenue: <BeatLoader color="#9e9e9e" size={10} />,

      // Utilization
      utilizationPerDateArray: [],
      averageLength: <BeatLoader color="#9e9e9e" size={10} />,

      // Active Sessions
      activeSessions: <BeatLoader color="#9e9e9e" size={10} />,

      //download menu
      anchorEl: null,
      open: false,
    };
  }

  // Get data and search for station status
  async componentDidMount() {
    if (
      Object.keys(this.props.stationStatus).length !== 0 &&
      Object.keys(this.props.sessions).length !== 0
    ) {
      this.setState(StationGroups(this.props.stationStatus)); // Sets groupList
      this._getData();
    }
  }

  componentDidUpdate(prevProps) {
    if (
      (prevProps.stationStatus !== this.props.stationStatus ||
        prevProps.active !== this.props.active ||
        prevProps.sessions !== this.props.sessions ||
        prevProps.billingHistory !== this.props.billingHistory) &&
      Object.keys(this.props.stationStatus).length !== 0 &&
      Object.keys(this.props.sessions).length !== 0
    ) {
      this.setState(StationGroups(this.props.stationStatus)); // Sets groupList
      this._getData();
    }
  }

  setExportData = (array) => {
    this.setState({ exportData: array });
  };

  // Switch chart displayed
  _switchDisplay = () => {
    if (this.state.selectedGraph === "totalSessions") {
      this.setState({
        bigChart: (
          <UniqueUsers
            timeframe={this.state.timeframe}
            start={this.state.start}
            end={this.state.end}
            victoryData={this.state.victoryData}
            victoryLabels={this.state.victoryLabels}
          />
        ),
      });
    } else if (this.state.selectedGraph === "energyConsumption") {
      this.setState({
        bigChart: (
          <EnergyConsumption
            timeframe={this.state.timeframe}
            start={this.state.start}
            end={this.state.end}
            victoryData={this.state.victoryData}
            victoryLabels={this.state.victoryLabels}
          />
        ),
      });
    } else if (this.state.selectedGraph === "revenue") {
      this.setState({
        bigChart: (
          <Revenue
            timeframe={this.state.timeframe}
            start={this.state.start}
            end={this.state.end}
            victoryData={this.state.victoryData}
            victoryLabels={this.state.victoryLabels}
          />
        ),
      });
    } else if (this.state.selectedGraph === "utilization") {
      this.setState({
        bigChart: (
          <Utilization
            timeframe={this.state.timeframe}
            start={this.state.start}
            end={this.state.end}
            victoryData={this.state.victoryData}
            victoryLabels={this.state.victoryLabels}
          />
        ),
      });
    }
  };

  // Determine which function to call for displaying data
  changeGraph = (value) => {
    this.setState({ selectedGraph: value }, () => {
      if (value === "chargerType" || value === "activeSessions")
        this._switchDisplay();
      else this.filterGraphsByDate();
    });
  };

  // Filter arrays by date range selected (TimeframeSelect)
  filterGraphsByDate = () => {
    // Filter arrays within date range
    _.each(
      {
        totalSessions: this.state.sessionsPerDateArray,
        energyConsumption: this.state.energyPerDateArray,
        revenue: this.state.revenuePerDateArray,
        utilization: this.state.utilizationPerDateArray,
      },
      (array, tab) => {
        var data = FilterData(
          array,
          tab,
          this.state.timeframe,
          this.state.start,
          this.state.end
        );

        // Set top total
        if (tab === "energyConsumption") {
          this.setState({
            energyConsumption: data.topTotal.toFixed(2),
            consumptionUnit: data.consumptionUnit,
          });
        } else if (tab === "totalSessions") {
          this.setState({
            totalSessions: data.topTotal,
          });
        } else if (
          tab === "revenue" &&
          this.props.billingHistory.length !== undefined
        ) {
          this.setState({
            revenue: `$${data.topTotal.toFixed(2)}`,
          });
        } else if (tab === "utilization") {
          // Average of the utilizations per day across a given time range
          this.setState({
            averageLength: `${parseFloat(
              data.topTotal / data.dataArray.length
            ).toFixed(2)}%`,
          });
        }

        // Update graph displayed
        if (tab === this.state.selectedGraph) {
          this.setState(
            {
              ...SetVictoryLabels(
                this.state.timeframe,
                this.state.start,
                this.state.end
              ), // Sets startTime, endTime, increment, format, victoryLabels
              victoryData: data.dataArray,
              exportData: data.exportData,
            },
            () => {
              this._switchDisplay();
            }
          );
        }
      }
    );
  };

  // Change date range and update arrays plus graph
  setTimeframe = (timeframe, start, end) => {
    if (start !== "" && end !== "") {
      this.setState({ start, end });
    }
    this.setState({ timeframe }, () => {
      this.filterGraphsByDate();
    });
  };

  // Refilter station selection
  handleSelection = (event) => {
    this.setState({ selectedGroup: event.target.value }, () => this._getData());
  };

  // Start of loop to populate array of charging sessions (in batches of 100)
  _getData() {
    // Filter stations via header filter
    var filtered = FilterStations(
      this.props.stationStatus,
      this.props.sessions,
      this.props.active,
      this.props.pctStatus,
      this.props.billingHistory,
      this.state.selectedGroup
    ); // Returns sessions, statuses, pctStatus, totalSessions, active
    var data = GetSessions(filtered.sessions, filtered.statuses); // Returns sessionArray, loading

    // Define timeframe for top totals and graph
    this.setTimeframe(this.state.timeframe, "", "");

    this.setState({
      ...CalculateActiveMetrics(filtered.active), // Sets activeConsumption, activeConsumptionUnit, activeSessions
      ...CalculateEnergyConsumption(data.sessionArray), // Sets energyPerDateArray
      ...CalculateRevenue(filtered.billing), // Sets revenuePerDateArray
      ...CalculateTotalSessions(data.sessionArray), // Sets sessionsPerDateArray
      ...CalculateUtilization(data.sessionArray), // Sets utilizationPerDateArray
      totalSessions: data.sessionArray.length,
      loading: false,
    });
  }

  StyledSelect = styled(OutlinedInput)(({ theme }) => ({
    "& .MuiOutlinedInput-input": {
      borderRadius: 4,
      position: "relative",
      fontSize: "1rem",
      padding: "10px 26px 10px 12px",
      "&:focus": {
        borderRadius: 4,
        borderColor: "#80bdff",
        boxShadow: "0 0 0 0.2rem rgba(0,123,255,.25)",
      },
    },
    "& .MuiOutlinedInput-notchedOutline": {
      fontSize: "2.15rem",
    },
  }));

  handleClose = () => {
    this.setState({ anchorEl: null, open: false });
  };
  handleClick = (event) => {
    this.setState({ open: true, anchorEl: event.currentTarget });
  };

  // Render main page and cards
  render() {
    // Determine if Station Group filter is visible
    var showGroupFilter =
      (this.state.groupList || []).length > 2 ? "flex" : "none";

    return (
      <div
        style={{
          backgroundColor: "#f7f8f9",
          height: "100%",
          width: "100%",
          paddingTop: "40px",
          paddingLeft: "60px",
          paddingRight: "60px",
          paddingBottom: "40px",
        }}
      >
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          style={{ marginBottom: "48px" }}
        >
          <Box display="flex" flexDirection="row" alignItems="center">
            <span
              style={{ color: "#404040", fontWeight: "bold", fontSize: "36px" }}
            >
              Metrics
            </span>
            <div
              style={{
                width: "300px",
                marginLeft: "50px",
                marginRight: "50px",
              }}
            >
              <FormControl
                variant="outlined"
                size="small"
                style={{
                  //width: "300px",
                  border: 0,
                  color: "#3E3E3E",
                  //height: "43px",
                  display: showGroupFilter,
                }}
              >
                <InputLabel
                  shrink
                  //style={{ fontSize: ".75rem", color: "#666666" }}
                >
                  Station Groups
                </InputLabel>
                <Select
                  value={this.state.selectedGroup}
                  onChange={this.handleSelection}
                  label="groups"
                  defaultValue="All Groups"
                  autoWidth={false}
                  sx={{ overflowX: "scroll" }}
                  //style={{ color: "#404040", width: "100%", fontSize: ".9rem" }}
                  input={<this.StyledSelect notched label={"groups"} />}
                  MenuProps={{
                    // PaperProps: { sx: { overflowX: "scroll !important" } },
                    MenuListProps: {
                      sx: { width: "300px !important" },
                    },
                  }}
                >
                  {this.state.groupList}
                </Select>
              </FormControl>
            </div>

            <Box
              display="flex"
              flexDirection="column"
              style={{ marginRight: "50px" }}
            >
              <span className="smallTitle">Current Load</span>
              <span className="bigDetail">
                {this.state.loading
                  ? this.state.activeConsumption
                  : this.state.activeConsumption +
                    " " +
                    this.state.activeConsumptionUnit}
              </span>
            </Box>
            <Box display="flex" flexDirection="column">
              <span className="smallTitle">Active Sessions</span>
              <span className="bigDetail">{this.state.activeSessions}</span>
            </Box>
          </Box>
          <Box display="flex">
            <Button
              id="basic-button"
              aria-controls={this.state.open ? "basic-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={this.state.open ? "true" : undefined}
              onClick={this.handleClick}
              style={{
                backgroundColor: "transparent",
                float: "right",
                marginTop: "10px",
                color: "#0C2739",
                marginRight: "-8px",
                paddingRight: "0px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid #c4c8c7",
                  width: "39px",
                  height: "28px",
                  borderRadius: 4,
                }}
              >
                <MoreHorizIcon size="large" style={{ color: "#0073a8" }} />
              </div>
            </Button>
            <Menu
              id="basic-menu"
              anchorEl={this.state.anchorEl}
              open={this.state.open}
              onClose={() => this.setState({ anchorEl: null, open: false })}
              MenuListProps={{
                "aria-labelledby": "basic-button",
              }}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
              <MenuItem
                onClick={() => ExportData(this.state.exportData)}
                className="menuItem"
              >
                Download Current View
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        <Box
          className="main-box"
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-around"
          style={{
            backgroundColor: "#fff",
            boxShadow: "0px 3px 3px #dedede",
            borderRadius: 4,
            padding: "35px",
            paddingRight: "0px",
          }}
        >
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            sx={{ width: "75%", paddingLeft: "2%", paddingRight: "5%" }}
            className="second-box"
          >
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="space-around"
              sx={{ width: "50%" }}
              className="two-piece-box"
            >
              <Box
                display="flex"
                flexDirection="column"
                style={{ marginRight: "50px", minWidth: "100px" }}
                className="left-side"
              >
                <span className="smallTitle">Total Sessions</span>
                <Box display="flex" flexDirection="row" alignItems="center">
                  <div className="blackIconCircle">
                    <HistoryIcon style={{ color: "#fff", fontSize: "14px" }} />
                  </div>
                  <span className="bigDetail">{this.state.totalSessions}</span>
                </Box>
              </Box>
              <Box
                display="flex"
                flexDirection="column"
                style={{ marginRight: "50px", minWidth: "180px" }}
                className="right-side"
              >
                <span className="smallTitle">Energy</span>
                <Box display="flex" flexDirection="row" alignItems="center">
                  <div className="blackIconCircle">
                    <BoltIcon style={{ color: "#fff", fontSize: "14px" }} />
                  </div>
                  <span className="bigDetail">
                    {this.state.loading
                      ? this.state.energyConsumption
                      : this.state.energyConsumption +
                        " " +
                        this.state.consumptionUnit}
                  </span>
                </Box>
              </Box>
            </Box>
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="space-around"
              sx={{ width: "50%" }}
              className="two-piece-bottom-box"
            >
              <Box
                display="flex"
                flexDirection="column"
                style={{ marginRight: "50px", minWidth: "170px" }}
                className="left-side"
              >
                <span className="smallTitle">Revenue</span>
                <Box display="flex" flexDirection="row" alignItems="center">
                  <div className="blackIconCircle">
                    <AttachMoneyIcon
                      style={{ color: "#fff", fontSize: "14px" }}
                    />
                  </div>
                  <span className="bigDetail">{this.state.revenue}</span>
                </Box>
              </Box>
              <Box
                display="flex"
                flexDirection="column"
                style={{ marginRight: "0px" }}
                className="right-side"
              >
                <span className="smallTitle">Utilization</span>
                <Box display="flex" flexDirection="row" alignItems="center">
                  <div className="blackIconCircle">
                    <BarChartIcon style={{ color: "#fff", fontSize: "14px" }} />
                  </div>
                  <span className="bigDetail">{this.state.averageLength}</span>
                </Box>
              </Box>
            </Box>
          </Box>
          <div
            style={{ width: 1, height: 70, backgroundColor: "#C4C8C7" }}
            className="gray-line"
          />
          <Box
            display="flex"
            flexDirection="row"
            sx={{ width: "25%" }}
            className="timeframe"
          >
            <TimeframeSelect
              setTimeframe={this.setTimeframe}
              selectedGraph={this.state.selectedGraph}
            />
          </Box>
        </Box>

        <Box style={{ marginTop: "25px", marginBottom: "25px" }}>
          <ButtonGroup>
            <Button
              onClick={() => this.changeGraph("totalSessions")}
              className={
                this.state.selectedGraph === "totalSessions"
                  ? "activeButtonGroup"
                  : "buttonGroup"
              }
            >
              Sessions
            </Button>
            <Button
              onClick={() => this.changeGraph("energyConsumption")}
              className={
                this.state.selectedGraph === "energyConsumption"
                  ? "activeButtonGroup"
                  : "buttonGroup"
              }
            >
              Energy
            </Button>
            <Button
              onClick={() => this.changeGraph("revenue")}
              className={
                this.state.selectedGraph === "revenue"
                  ? "activeButtonGroup"
                  : "buttonGroup"
              }
            >
              Revenue
            </Button>
            <Button
              onClick={() => this.changeGraph("utilization")}
              className={
                this.state.selectedGraph === "utilization"
                  ? "activeButtonGroup"
                  : "buttonGroup"
              }
            >
              Utilization
            </Button>
          </ButtonGroup>
        </Box>
        <div className="row-12 mr-1 ml-0 mt-3 mb-3" style={{ height: "590px" }}>
          <div style={{ height: "100%" }}>
            <Card
              raised
              style={{
                paddingBottom: "10px",
                backgroundColor: "#ffffff",
                borderRadius: 5,
                width: "100%",
                height: "100%",
              }}
            >
              {this.state.bigChart}
            </Card>
          </div>
        </div>
      </div>
    );
  }
}
export default HomeContainer;
